<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="editMode" class="card mb-3">
            <div class="card-body">
                <div class="col-12 d-flex flex-wrap justify-content-between">
                    <div>
                        <h5>Number: {{ order.number }}</h5>
                        <h6>
                            Created:
                            {{ order.createdAt | formatDate }}
                        </h6>
                        <h6>
                            Is exported:
                            <b-badge
                                pill
                                class="mx-1 px-2"
                                :variant="
                                    order.isExported ? 'success' : 'danger'
                                "
                            >
                                {{ order.isExported ? 'YES' : 'NO' }}
                            </b-badge>
                        </h6>
                    </div>

                    <div class="form-group d-flex align-items-start">
                        <button
                            type="button"
                            class="btn btn-info waves-effect waves-light ml-2"
                            :disabled="!isRefundPossible"
                            @click.prevent="refund"
                        >
                            {{ isOrderRefunded ? 'Refunded' : 'Refund' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="order.status" class="card mb-3">
            <div class="card-body">
                <div class="form-group col-12 card-title">
                    <b>Status</b>
                </div>
                <div
                    v-if="isChangeStatusAvailable"
                    class="form-group col-12 d-flex align-items-center"
                >
                    <div style="flex: 1">
                        <status v-model="status" />
                    </div>
                    <button
                        class="col-2 ml-1 btn btn-success waves-effect waves-ligh"
                        type="button"
                        @click="save"
                    >
                        Change
                    </button>
                </div>
                <history v-if="statusHistory.length" :history="statusHistory" />
            </div>
        </div>

        <div v-if="productsList.length" class="card mb-3">
            <div class="card-body">
                <div class="form-group col-12 card-title">
                    <b>Products</b>
                </div>
                <products :products="productsList" class="mb-3" />
            </div>
        </div>

        <div v-if="!editMode" class="card mb-3">
            <div class="card-body">
                <div class="form-group col-12 card-title">
                    <b>Supplier</b>
                </div>
                <div class="form-group col-12">
                    <shop-selector v-model="order.shopId" />
                </div>
            </div>
        </div>

        <div v-if="!editMode && order.shopId" class="card mb-3">
            <div class="card-body">
                <div class="form-group col-12 card-title">
                    <b>Variants</b>
                </div>
                <div class="form-group col-12">
                    <variants-selector
                        v-model="order.productVariants"
                        :shop-id="order.shopId"
                    />
                    <div class="error-box my-2">
                        {{ getError('productVariants') }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!editMode && order.shopId" class="card mb-3">
            <div class="card-body">
                <div class="form-group col-12 card-title">
                    <b>User</b>
                </div>
                <div class="form-group col-12">
                    <user-selector @input="userChanged" />
                </div>

                <div v-if="agudaMemberError" class="error-box my-2">
                    You have chosen some products that are only for Aguda
                    members but this user is not an Aguda member.
                </div>
            </div>
        </div>

        <div v-if="editMode || order.userId" class="card">
            <div class="card-body">
                <div class="form-group col-12 card-title">
                    <b>General</b>
                </div>
                <div class="d-flex flex-wrap">
                    <div v-if="editMode" class="form-group col-12">
                        <label>Is exported</label>
                        <b-form-checkbox
                            v-model="isExported"
                            switch
                            :disabled="order.isExported"
                        />
                    </div>
                    <div class="form-group col-md-6">
                        <label>Total Price</label>
                        <input
                            :value="totalPrice | formatPrice"
                            type="text"
                            class="form-control"
                            dir="auto"
                            disabled
                        />
                    </div>
                    <div v-if="editMode" class="form-group col-md-6">
                        <label>Used stars</label>
                        <input
                            :value="order.starsUsed"
                            type="text"
                            class="form-control"
                            dir="auto"
                            disabled
                        />
                    </div>
                    <div class="form-group col-md-6">
                        <label>Payment Method</label>
                        <v-select
                            v-model="order.paymentMethod"
                            :options="paymentMethods"
                            :class="{ 'is-invalid': hasError('paymentMethod') }"
                            placeholder="Payment method"
                            :disabled="editMode"
                        />

                        <div class="invalid-feedback">
                            {{ getError('paymentMethod') }}
                        </div>
                    </div>
                    <div v-if="order.shop" class="form-group col-12">
                        <label>Supplier</label>
                        <input
                            :value="order.shop.name"
                            type="text"
                            class="form-control"
                            dir="auto"
                            disabled
                        />
                    </div>

                    <div class="form-group col-12 card-title mt-4">
                        <b>Customer Information</b>
                        <router-link
                            v-if="order.user"
                            :to="`/users/${order.user.id}/edit`"
                            class="ml-2"
                            target="_blank"
                        >
                            <i class="fas fa-external-link-alt" />
                        </router-link>
                    </div>
                    <div class="form-group col-12">
                        <label>Full Name</label>
                        <input
                            v-model="order.fullName"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': hasError('fullName') }"
                            dir="auto"
                        />
                        <div class="invalid-feedback">
                            {{ getError('fullName') }}
                        </div>
                    </div>
                    <div class="form-group col-md-6">
                        <label>Email</label>
                        <input
                            v-model="order.email"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': hasError('email') }"
                            dir="auto"
                        />
                        <div class="invalid-feedback">
                            {{ getError('email') }}
                        </div>
                    </div>
                    <div class="form-group col-md-6">
                        <label>Phone number</label>
                        <input
                            v-model="order.phone"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': hasError('phone') }"
                        />
                        <div class="invalid-feedback">
                            {{ getError('phone') }}
                        </div>
                    </div>
                    <div class="form-group col-md-6">
                        <label>Address</label>
                        <input
                            v-model="order.address"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': hasError('address') }"
                        />
                        <div class="invalid-feedback">
                            {{ getError('address') }}
                        </div>
                    </div>
                    <div class="form-group col-12 mt-4">
                        <label><b>Note</b></label>
                        <textarea
                            v-model="order.note"
                            type="text"
                            class="form-control rtl"
                            dir="auto"
                        />
                    </div>
                </div>
                <div class="form-group col-12 mt-3">
                    <button
                        type="submit"
                        class="btn btn-success waves-effect waves-light"
                        @click="save"
                    >
                        {{ editMode ? 'Update' : 'Save' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required, email } from 'vuelidate/lib/validators';
import Status from '@/components/orders/Status';
import History from '@/components/orders/History';
import Products from '@/components/orders/Products';
import UserSelector from '@/components/users/Selector';
import ShopSelector from '@/components/shops/Selector';
import validatePhoneFormat from '@/helpers/validatePhoneFormat';
import VariantsSelector from '@/components/orders/VariantsSelector';
import OrderValuesCalculator from '@/../../common/orders/OrderValuesCalculator';

export default {
    page: {
        title: 'Manage Order'
    },

    validations: {
        order: {
            fullName: {
                required
            },
            email: {
                required,
                email
            },
            phone: {
                required,
                isFormatValid: v => validatePhoneFormat(v)
            },
            address: {
                required
            },
            productVariants: {
                atLeastOneItem(val) {
                    if (this.editMode) {
                        return true;
                    }

                    return !!(val && val.length);
                }
            },
            paymentMethod: {
                required
            }
        }
    },

    components: {
        VariantsSelector,
        ShopSelector,
        UserSelector,
        Products,
        History,
        Status
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit order' : 'Add order',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Orders',
                    to: '/orders'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            order: {
                productVariants: []
            },
            status: '',
            isExported: false,
            id,
            editMode: !!id,
            serverErrors: [],
            isAgudaMemberOrder: false
        };
    },

    computed: {
        ...mapGetters({
            STATUS_REFUNDED: 'orders/REFUNDED',
            STATUS_CANCELLED: 'orders/CANCELLED',
            ROLE_AGUDA: 'roles/AGUDA',
            PAYMENT_METHOD_CREDIT: 'orders/PAYMENT_METHOD_CREDIT',
            paymentMethods: 'orders/PAYMENT_METHODS'
        }),

        productsList() {
            const { productVariants } = this.order;

            if (!this.editMode || !productVariants) {
                return [];
            }

            return productVariants.map(productVariant => ({
                id: productVariant.id,
                sku: productVariant.sku,
                attributes: productVariant.attributes,
                name: productVariant.product.name,
                productId: productVariant.product.id,
                shopId: productVariant.product.shopId,
                totalPrice: productVariant.Order2ProductVariant.totalPrice,
                quantity: productVariant.Order2ProductVariant.quantity
            }));
        },

        statusHistory() {
            const { history } = this.order;

            if (!history) {
                return [];
            }

            return history.filter(historyItem => historyItem.value.status);
        },

        totalPrice() {
            if (this.editMode) {
                return this.order?.totalPrice;
            }

            const { total } = new OrderValuesCalculator(
                this.order.productVariants || [],
                this.isAgudaMemberOrder
            );

            return total;
        },

        agudaMemberError() {
            if (this.editMode) {
                return false;
            }

            return (
                !this.isAgudaMemberOrder &&
                this.order.productVariants.some(
                    ({ product }) => product.onlyForAgudaMembers
                )
            );
        },

        isOrderRefunded() {
            return this.status === this.STATUS_REFUNDED;
        },

        isOrderCancelled() {
            return this.status === this.STATUS_CANCELLED;
        },

        isChangeStatusAvailable() {
            return !(this.isOrderRefunded || this.isOrderCancelled);
        },

        isRefundPossible() {
            return (
                !this.isOrderRefunded &&
                this.order.paymentMethod === this.PAYMENT_METHOD_CREDIT
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.order = await this.getOrder(this.id);
                this.status = this.order.status;
                this.isExported = this.order.isExported;
            } catch (err) {
                console.error(err);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            updateOrder: 'orders/update',
            addOrder: 'orders/store',
            getOrder: 'orders/show',
            refundOrder: 'orders/refund'
        }),

        async save() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid || this.agudaMemberError) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addOrder({
                        ...this.order,
                        totalPrice: this.totalPrice
                    });

                    this.$router.push(`/orders/${added.id}/edit`);
                } else {
                    this.order = await this.updateOrder({
                        id: this.id,
                        data: {
                            ...this.order,
                            status: this.status,
                            isExported: this.isExported
                        }
                    });

                    this.$toaster('Order has been updated!');
                }
            } catch (error) {
                this.handleError(error);
            }
        },

        async refund() {
            try {
                const history = await this.refundOrder(this.order.id);

                this.order.history = history;
                this.status = this.STATUS_REFUNDED;

                this.$toaster('Succesfully made refund!');
            } catch (error) {
                this.handleError(error);
            }
        },

        userChanged({ id, fullName, email, phone, roles }) {
            this.order = {
                ...this.order,
                userId: id,
                fullName,
                email,
                phone
            };

            this.isAgudaMemberOrder = roles.some(
                role => role.name === this.ROLE_AGUDA
            );
        },

        hasError(key) {
            return (
                this.$v.order[key].$error ||
                this.serverErrors.find(error => error.param === key)
            );
        },

        getError(key) {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.order,
                this.serverErrors,
                key
            );
        },

        handleError(error) {
            const { response } = error;

            if (response) {
                const { data, status } = response;

                if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                    this.$toasterError(data || data.message);

                    return;
                }

                if (status === HTTP.BAD_REQUEST && data.errors) {
                    this.serverErrors = data.errors;

                    this.$toasterError('Recheck your form.');

                    return;
                }
            }

            console.error(error);

            this.$toasterError();
        }
    }
};
</script>
